import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getAsset, listActiveSales } from "../api/atomicApi";
import { buySale, listAssetForSale, redeemBackedAsset } from "../api/actions";
import { logActivity } from "../api/activityLog";
import { assetImageHash, formatAssetName, getBackingInfo } from "../utils/backing";
import { useWallet } from "../wallet/WalletContext";
import type { AtomicAsset, MarketSale } from "../types";
import { IpfsImage } from "../components/IpfsImage";
import { attachEscrowBacking } from "../api/nftdropsApi";
import { showSuccess, showError, showInfo, confirmAction } from "../components/Dialog";

export function AssetDetail() {
  const { assetId } = useParams<{ assetId: string }>();
  const { session, account, connect } = useWallet();
  const navigate = useNavigate();

  const [asset, setAsset] = useState<AtomicAsset | null>(null);
  const [sale, setSale] = useState<MarketSale | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [listPrice, setListPrice] = useState("");
  const [listSymbol, setListSymbol] = useState<"WAX" | "EOS">("WAX");
  const [copied, setCopied] = useState(false);

  // A one-tap "copy this exact page's link" — the point is making it the
  // path of least resistance to share the WAXCreator link (which earns
  // this site's marketplace commission on any resulting sale) rather than
  // defaulting to whatever link AtomicHub shows for the same asset.
  async function handleShare() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showInfo(window.location.href, "Copy this link");
    }
  }

  useEffect(() => {
    if (!assetId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getAsset(assetId).then(async (a) => (await attachEscrowBacking([a]))[0]),
      listActiveSales({ assetId }).catch(() => []),
    ])
      .then(([assetData, sales]) => {
        if (cancelled) return;
        setAsset(assetData);
        setSale(sales[0] ?? null);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load asset");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [assetId]);

  if (loading) return <div className="page state-message">Loading asset…</div>;
  if (error) return <div className="page state-message state-message--error">{error}</div>;
  if (!asset) return <div className="page state-message">Asset not found.</div>;

  const backing = getBackingInfo(asset);
  const imgHash = assetImageHash(asset);
  const isOwner = asset.owner === account;

  async function ensureSession() {
    if (session) return session;
    await connect();
    return undefined;
  }

  async function handleBuy() {
    const s = await ensureSession();
    if (!s || !sale || !asset) return;
    setBusy(true);
    try {
      await buySale(s, sale);
      logActivity({
        type: "buy",
        account: s.actor.toString(),
        assetId: asset.asset_id,
        collectionName: asset.collection?.collection_name,
        amount: `${(Number(sale.price.amount) / 10 ** sale.price.token_precision).toFixed(sale.price.token_precision)} ${sale.price.token_symbol}`,
      });
      setSale(null);
    } catch (err) {
      showError(err, "Purchase failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleRedeem() {
    const s = await ensureSession();
    if (!s || !asset) return;
    if (
      !(await confirmAction({
        title: "Redeem this backed NFT?",
        message: (
          <>
            This <strong>permanently burns</strong> the NFT and releases{" "}
            <strong>
              {backing?.amount} {backing?.symbol}
            </strong>{" "}
            from escrow to your wallet. This can't be undone.
          </>
        ),
        confirmLabel: "Burn & redeem",
        danger: true,
      }))
    )
      return;
    setBusy(true);
    try {
      await redeemBackedAsset(s, asset.asset_id);
      logActivity({
        type: "redeem",
        account: s.actor.toString(),
        assetId: asset.asset_id,
        collectionName: asset.collection?.collection_name,
        amount: backing ? `${backing.amount} ${backing.symbol}` : undefined,
      });
      await showSuccess(
        <>
          <strong>
            {backing?.amount} {backing?.symbol}
          </strong>{" "}
          has been released from escrow to your wallet, and the NFT has been burned.
        </>,
        "Redeemed"
      );
      navigate("/inventory");
    } catch (err) {
      showError(err, "Redeem failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleList(e: FormEvent) {
    e.preventDefault();
    const s = await ensureSession();
    if (!s || !asset || !listPrice) return;
    setBusy(true);
    try {
      await listAssetForSale(s, {
        assetId: asset.asset_id,
        collectionName: asset.collection.collection_name,
        price: listPrice,
        symbol: listSymbol,
      });
      logActivity({
        type: "list_for_sale",
        account: s.actor.toString(),
        assetId: asset.asset_id,
        collectionName: asset.collection?.collection_name,
        amount: `${listPrice} ${listSymbol}`,
      });
      showSuccess("Your NFT is now listed on AtomicMarket.", "Listed for sale");
    } catch (err) {
      showError(err, "Listing failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page page--detail">
      <div className="asset-detail__media">
        <IpfsImage hash={imgHash} alt={formatAssetName(asset)} />
      </div>

      <div className="asset-detail__info">
        <div className="asset-detail__collection">{asset.collection?.name || asset.collection?.collection_name}</div>
        <h1>{formatAssetName(asset)}</h1>
        <div className="asset-detail__owner">Owned by {asset.owner}</div>

        <button className="btn btn--outline btn--small" onClick={handleShare} style={{ marginBottom: 12 }}>
          {copied ? "Link copied!" : "Copy share link"}
        </button>

        {backing && (
          <div className="callout">
            🔒 Backed by <strong>{backing.amount} {backing.symbol}</strong> — burn this NFT to redeem the tokens.
          </div>
        )}

        {sale && (
          <div className="asset-detail__price-block">
            <div className="asset-detail__price">
              {(Number(sale.price.amount) / 10 ** sale.price.token_precision).toFixed(sale.price.token_precision)}{" "}
              {sale.price.token_symbol}
            </div>
            {!isOwner && (
              <button className="btn btn--primary" onClick={handleBuy} disabled={busy}>
                {busy ? "Buying…" : "Buy now"}
              </button>
            )}
          </div>
        )}

        {isOwner && (
          <div className="asset-detail__owner-actions">
            {backing && (
              <button className="btn btn--outline" onClick={handleRedeem} disabled={busy}>
                {busy ? "Working…" : `Redeem for ${backing.amount} ${backing.symbol}`}
              </button>
            )}

            {!sale && (
              <form className="list-form" onSubmit={handleList}>
                <input
                  type="number"
                  min="0"
                  step="0.00000001"
                  placeholder="Price"
                  value={listPrice}
                  onChange={(e) => setListPrice(e.target.value)}
                  required
                />
                <select value={listSymbol} onChange={(e) => setListSymbol(e.target.value as "WAX" | "EOS")}>
                  <option value="WAX">WAX</option>
                  <option value="EOS">EOS</option>
                </select>
                <button className="btn btn--primary" type="submit" disabled={busy}>
                  {busy ? "Listing…" : "List for sale"}
                </button>
              </form>
            )}
          </div>
        )}

        {asset.data && Object.keys(asset.data).length > 0 && (
          <div className="attributes">
            <h2>Attributes</h2>
            <dl>
              {Object.entries(asset.data).map(([key, value]) => (
                <div className="attributes__row" key={key}>
                  <dt>{key}</dt>
                  <dd>{String(value)}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </div>
    </div>
  );
}
